import { useEffect, useRef, useState, useCallback } from "react";
import maplibregl, { Map as MapLibreMap, Marker } from "maplibre-gl";
import MaplibreWorker from "maplibre-gl/dist/maplibre-gl-csp-worker";
import "maplibre-gl/dist/maplibre-gl.css";
import "./explore.css";
import { CARTO_STYLE_URL, neonizeCartoStyle } from "./gameStyle";

// FIX PRODUZIONE (basemap vuota): il worker INLINE di MapLibre si rompe nel bundle
// minificato di CRA (Terser perde gli helper → "ReferenceError: s is not defined"),
// quindi i vector tile vengono scaricati ma MAI parsati e la cartografia resta vuota
// (mentre i marker DOM del main thread restano visibili). Forzando il worker CSP come
// modulo separato, webpack lo bundla correttamente e il parsing funziona in produzione.
try { if (MaplibreWorker) maplibregl.workerClass = MaplibreWorker; } catch (e) { /* noop */ }
import { dayPhase } from "../../lib/perf";

function webglSupported() {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl") || c.getContext("experimental-webgl")));
  } catch (e) { return false; }
}

/**
 * Motore mappa riutilizzabile con stato di caricamento/errore e retry.
 * Priorità: la mappa deve caricarsi SEMPRE o mostrare un errore con "Riprova",
 * mai una schermata vuota/bloccata.
 */
export default function MapEngine({ layers = [], lines = [], center = [7.628, 45.216], zoom = 13.6, pitch = 45, focus, onReady, ghosts = 0, ghostSeeds = [], ghostRoutes = [], trafficRoutes = [] }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const removalTimer = useRef(null);
  const watchdog = useRef(null);
  const autoRetried = useRef(false);
  const criticalErr = useRef(0);
  const store = useRef(new Map());
  const layersRef = useRef(layers);
  const statusRef = useRef("loading");
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [reloadKey, setReloadKey] = useState(0);
  const [phase, setPhase] = useState(dayPhase());
  layersRef.current = layers;
  statusRef.current = status;

  // Ciclo giorno/notte: tint atmosferico leggero (solo CSS, nessun re-render mappa).
  useEffect(() => {
    const t = setInterval(() => setPhase(dayPhase()), 600000);
    return () => clearInterval(t);
  }, []);

  const hardReset = useCallback(() => {
    if (watchdog.current) { clearTimeout(watchdog.current); watchdog.current = null; }
    const m = mapRef.current;
    try { m && m.remove(); } catch (e) {}
    mapRef.current = null;
    store.current.clear();
  }, []);

  const retry = useCallback((auto = false) => {
    if (!auto) { autoRetried.current = false; criticalErr.current = 0; }
    hardReset();
    setStatus("loading");
    setReloadKey((k) => k + 1);
  }, [hardReset]);

  useEffect(() => {
    if (removalTimer.current) { clearTimeout(removalTimer.current); removalTimer.current = null; }
    if (mapRef.current) return;
    if (!webglSupported()) { setStatus("error"); return; }

    let map;
    try {
      map = new MapLibreMap({
        container: ref.current, style: CARTO_STYLE_URL, center, zoom,
        pitch, bearing: -20, attributionControl: false, maxPitch: 72,
      });
    } catch (e) { setStatus("error"); return; }

    const onFailure = () => {
      if (watchdog.current) { clearTimeout(watchdog.current); watchdog.current = null; }
      if (!autoRetried.current) { autoRetried.current = true; setTimeout(() => retry(true), 500); }
      else { setStatus("error"); }
    };

    map.dragRotate.enable();
    map.touchZoomRotate.enableRotation();
    map.on("load", () => {
      if (watchdog.current) { clearTimeout(watchdog.current); watchdog.current = null; }
      try { neonizeCartoStyle(map); } catch (e) { /* stile decorativo non bloccante */ }
      try { map.resize(); } catch (e) {}
      setStatus("ready");
      onReady && onReady(map);
    });
    map.once("idle", () => { try { map.resize(); } catch (e) {} });
    map.on("error", (e) => {
      const err = e && e.error;
      const msg = (err && err.message) || "unknown";
      // Logging strutturato: identifica subito problemi di style/source/tile/worker.
      try { console.warn("[MapLibre error]", { message: msg, sourceId: e && e.sourceId, url: (e && e.url) || (err && err.url), stack: err && err.stack }); } catch (_) {}
      // Stile non caricato affatto → fallimento critico (retry).
      if (/style|sprite|glyphs|failed to fetch|networkerror/i.test(msg) && !map.isStyleLoaded()) { onFailure(); return; }
      // Worker/parsing rotto (es. bundle minificato): i tile non vengono mai disegnati.
      // Se ricorre, mostra il fallback invece di una mappa vuota con marker sospesi.
      if (/is not defined|reading '|worker|failed to parse|out of memory/i.test(msg)) {
        criticalErr.current += 1;
        if (criticalErr.current >= 4) onFailure();
      }
    });
    // Watchdog: se entro 12s la mappa non è pronta → retry automatico, poi errore.
    watchdog.current = setTimeout(() => { if (!map.isStyleLoaded()) onFailure(); }, 12000);

    mapRef.current = map;
    const t1 = setTimeout(() => { try { map.resize(); } catch (e) {} }, 300);
    const t2 = setTimeout(() => { try { map.resize(); } catch (e) {} }, 1200);
    let ro;
    try {
      ro = new ResizeObserver(() => { try { map.resize(); } catch (e) {} });
      if (ref.current) ro.observe(ref.current);
    } catch (e) {}
    return () => {
      clearTimeout(t1); clearTimeout(t2);
      if (watchdog.current) { clearTimeout(watchdog.current); watchdog.current = null; }
      try { ro && ro.disconnect(); } catch (e) {}
      const m = mapRef.current;
      removalTimer.current = setTimeout(() => {
        try { m && m.remove(); } catch (e) {}
        mapRef.current = null;
        store.current.clear();
      }, 400);
    };
    // eslint-disable-next-line
  }, [reloadKey]);

  // Declutter dei label "comune": nasconde progressivamente in base allo zoom
  // (item.minZoom) e risolve le sovrapposizioni per priorità (item.priority).
  // Solo toggle di classe CSS → nessuna ricreazione di marker, leggero su mobile.
  const applyDeclutter = useCallback(() => {
    const map = mapRef.current;
    if (!map || statusRef.current !== "ready") return;
    const z = map.getZoom();
    const entries = [];
    for (const [, v] of store.current) if (v.declutter && v.marker) entries.push(v);
    if (!entries.length) return;
    entries.forEach((v) => { v._hide = false; });
    const visible = entries.filter((v) => {
      const mz = v.item && v.item.minZoom;
      const xz = v.item && v.item.maxZoom;
      if ((mz != null && z < mz) || (xz != null && z > xz)) { v._hide = true; return false; }
      return true;
    });
    visible.sort((a, b) => (((b.item && b.item.priority) || 0) - ((a.item && a.item.priority) || 0))
      || (((b.item && b.item.count) || 0) - ((a.item && a.item.count) || 0)));
    const placed = [];
    visible.forEach((v) => {
      let p; try { p = map.project(v.marker.getLngLat()); } catch (e) { return; }
      const el = v.marker.getElement();
      const w = ((el && el.offsetWidth) || 72) + 16, h = ((el && el.offsetHeight) || 32) + 12;
      const box = { x1: p.x - w / 2, y1: p.y - h / 2, x2: p.x + w / 2, y2: p.y + h / 2 };
      const hit = placed.some((q) => !(box.x2 < q.x1 || box.x1 > q.x2 || box.y2 < q.y1 || box.y1 > q.y2));
      if (hit) v._hide = true; else placed.push(box);
    });
    entries.forEach((v) => { const el = v.marker.getElement(); if (el) el.classList.toggle("cm--hidden", !!v._hide); });
  }, []);

  // Sincronizza i marker con culling per viewport: renderizza solo ciò che è
  // nell'area visibile (+padding) per prestazioni fluide anche con molti POI.
  const syncMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map || statusRef.current !== "ready") return;
    let pb = null;
    try {
      const b = map.getBounds();
      const w = b.getWest(), e = b.getEast(), s = b.getSouth(), n = b.getNorth();
      const dx = (e - w) * 0.3, dy = (n - s) * 0.3;
      pb = { w: w - dx, e: e + dx, s: s - dy, n: n + dy };
    } catch (err) {}
    const seen = new Set();
    layersRef.current.forEach((layer) => {
      (layer.items || []).forEach((item) => {
        if (pb && !layer.noCull && (item.lng < pb.w || item.lng > pb.e || item.lat < pb.s || item.lat > pb.n)) return;
        const key = `${layer.id}:${item.id}`;
        seen.add(key);
        const sig = layer.sig ? layer.sig(item) : JSON.stringify([item.lat, item.lng, item.has_offer, item.offer_title, item.name]);
        const existing = store.current.get(key);
        if (existing && existing.sig === sig) { existing.marker.setLngLat([item.lng, item.lat]); existing.item = item; return; }
        if (existing) existing.marker.remove();
        const marker = new Marker({ element: layer.build(item), anchor: layer.anchor || "bottom" })
          .setLngLat([item.lng, item.lat]).addTo(map);
        store.current.set(key, { marker, sig, declutter: !!layer.declutter, item });
      });
    });
    for (const [key, v] of store.current) {
      if (!seen.has(key)) { v.marker.remove(); store.current.delete(key); }
    }
    applyDeclutter();
  }, [applyDeclutter]);

  useEffect(() => { syncMarkers(); }, [layers, status, syncMarkers]);

  useEffect(() => {
    const m = mapRef.current;
    if (!m || status !== "ready") return;
    const onMove = () => syncMarkers();
    m.on("moveend", onMove);
    return () => { try { m.off("moveend", onMove); } catch (e) {} };
  }, [status, syncMarkers]);

  // Ricalcola il declutter DURANTE lo zoom (throttle via rAF) così nomi/attività
  // compaiono progressivamente man mano che si ingrandisce.
  useEffect(() => {
    const m = mapRef.current;
    if (!m || status !== "ready") return;
    let raf = null;
    const onZoom = () => { if (raf) return; raf = requestAnimationFrame(() => { raf = null; applyDeclutter(); }); };
    m.on("zoom", onZoom);
    return () => { try { m.off("zoom", onZoom); } catch (e) {} if (raf) cancelAnimationFrame(raf); };
  }, [status, applyDeclutter]);

  useEffect(() => {
    const m = mapRef.current;
    if (m && status === "ready" && focus) m.flyTo({ center: [focus.lng, focus.lat], zoom: focus.zoom || 16, pitch: focus.pitch ?? 56, duration: focus.duration ?? 850, curve: 1.5, essential: true });
  }, [focus, status]);

  useEffect(() => {
    if (status !== "ready") return;
    const fc = { type: "FeatureCollection", features: (lines || []).map((l) => ({ type: "Feature", geometry: { type: "LineString", coordinates: l.coords }, properties: {} })) };
    let animId = null, cancelled = false, tries = 0;
    const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const DASH_SEQ = [
      [0, 4, 3], [0.5, 4, 2.5], [1, 4, 2], [1.5, 4, 1.5], [2, 4, 1], [2.5, 4, 0.5],
      [3, 4, 0], [0, 0.5, 4, 2.5], [0, 1, 4, 2], [0, 1.5, 4, 1.5], [0, 2, 4, 1], [0, 2.5, 4, 0.5], [0, 3, 4, 0],
    ];
    const apply = (m) => {
      const src = m.getSource("deco-lines");
      if (src) { src.setData(fc); }
      else {
        m.addSource("deco-lines", { type: "geojson", data: fc });
        m.addLayer({ id: "deco-lines-glow", type: "line", source: "deco-lines", layout: { "line-cap": "round" }, paint: { "line-color": "#00E676", "line-width": 6, "line-opacity": 0.16, "line-blur": 8 } });
        m.addLayer({ id: "deco-lines-core", type: "line", source: "deco-lines", layout: { "line-cap": "round" }, paint: { "line-color": "#7CFFC4", "line-width": 1.6, "line-opacity": 0.6, "line-dasharray": [1, 2.5] } });
      }
      if (!reduce && (lines || []).length) {
        let step = 0, last = 0;
        const frame = (t) => {
          if (cancelled) return;
          if (t - last > 55) {
            last = t;
            try { m.setPaintProperty("deco-lines-core", "line-dasharray", DASH_SEQ[step]); } catch (e) {}
            step = (step + 1) % DASH_SEQ.length;
          }
          animId = requestAnimationFrame(frame);
        };
        animId = requestAnimationFrame(frame);
      }
    };
    const boot = () => {
      if (cancelled) return;
      const m = mapRef.current;
      if (!m || !m.isStyleLoaded()) { if (tries++ < 80) setTimeout(boot, 120); return; }
      apply(m);
    };
    boot();
    return () => { cancelled = true; if (animId) cancelAnimationFrame(animId); };
  }, [lines, status]);

  // Rider "fantasma" che seguono STRADE REALI (percorsi OSRM forniti dal backend) + scia.
  // Rendono la mappa VIVA anche senza consegne reali e diminuiscono quando arrivano ordini
  // reali. Stile identico ai rider reali (cyan) → l'utente vede semplicemente rider in giro.
  // Fallback (OSRM non disponibile): vagabondaggio in linea retta tra i seed.
  useEffect(() => {
    if (status !== "ready") return;
    const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const n = Math.max(0, Math.min(14, ghosts | 0));
    const routes = (ghostRoutes || []).filter((r) => Array.isArray(r) && r.length >= 2);
    const seeds = (ghostSeeds || []).filter((p) => Array.isArray(p) && p.length === 2);
    const emptyFC = { type: "FeatureCollection", features: [] };
    const COS = Math.cos((45.22 * Math.PI) / 180);
    // Traffico immaginario: i ghost rallentano vicino ai tratti congestionati (segmenti rossi).
    const congestion = (trafficRoutes || []).flat().filter((c) => Array.isArray(c) && c.length === 2);
    const TRAFFIC_R2 = 0.00065 * 0.00065; // raggio di influenza (~gradi, corretto per lat)
    const slowFactor = (lng, lat) => {
      if (!congestion.length) return 1;
      let min2 = Infinity;
      for (let i = 0; i < congestion.length; i++) {
        const dx = (congestion[i][0] - lng) * COS, dy = congestion[i][1] - lat;
        const d2 = dx * dx + dy * dy;
        if (d2 < min2) min2 = d2;
      }
      return min2 < TRAFFIC_R2 ? 0.18 : 1; // in coda: rallenta a ~18%
    };
    let raf = null, cancelled = false, tries = 0;
    const ensure = (m) => {
      if (!m.getSource("ghost-trails")) {
        m.addSource("ghost-trails", { type: "geojson", data: emptyFC });
        m.addLayer({ id: "ghost-trail", type: "line", source: "ghost-trails", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#94A3B8", "line-width": 3, "line-opacity": 0.18, "line-blur": 2 } });
      }
      if (!m.getSource("ghosts")) {
        m.addSource("ghosts", { type: "geojson", data: emptyFC });
        m.addLayer({ id: "ghost-glow", type: "circle", source: "ghosts", paint: { "circle-radius": 13, "circle-color": "#CBD5E1", "circle-opacity": 0.12, "circle-blur": 1 } });
        m.addLayer({ id: "ghost-core", type: "circle", source: "ghosts", paint: { "circle-radius": 5, "circle-color": "#E2E8F0", "circle-opacity": 0.5, "circle-stroke-color": "rgba(226,232,240,0.35)", "circle-stroke-width": 1.5 } });
      }
    };
    // Camminatore lungo una polilinea stradale (dist in gradi corretti per latitudine).
    const pickRoute = () => routes[Math.floor(Math.random() * routes.length)];
    const makeWalker = (route) => {
      const segs = []; let total = 0;
      for (let i = 0; i < route.length - 1; i++) {
        const a = route[i], b = route[i + 1];
        const dx = (b[0] - a[0]) * COS, dy = b[1] - a[1];
        const len = Math.hypot(dx, dy) || 1e-9;
        segs.push({ a, b, len, acc: total }); total += len;
      }
      return { route, segs, total, dist: Math.random() * total, spd: 0.0000003 + Math.random() * 0.0000002, trail: [] };
    };
    const posAt = (w) => {
      const d = Math.max(0, Math.min(w.total, w.dist));
      for (let i = 0; i < w.segs.length; i++) {
        const s = w.segs[i];
        if (d <= s.acc + s.len || i === w.segs.length - 1) {
          const t = s.len > 0 ? (d - s.acc) / s.len : 0;
          return [s.a[0] + (s.b[0] - s.a[0]) * t, s.a[1] + (s.b[1] - s.a[1]) * t];
        }
      }
      return w.route[w.route.length - 1];
    };
    const makeFallback = () => {
      const lngs = seeds.map((s) => s[0]), lats = seeds.map((s) => s[1]);
      const minLng = Math.min(...lngs), maxLng = Math.max(...lngs), minLat = Math.min(...lats), maxLat = Math.max(...lats);
      const randPt = () => [minLng + Math.random() * (maxLng - minLng), minLat + Math.random() * (maxLat - minLat)];
      return { fallback: true, a: randPt(), b: randPt(), t: Math.random(), spd: 0.00003 + Math.random() * 0.00002, trail: [], randPt };
    };
    const animate = (m) => {
      const useRoads = routes.length > 0;
      const arr = Array.from({ length: n }, () => (useRoads ? makeWalker(pickRoute()) : makeFallback()));
      let last = performance.now();
      const frame = (now) => {
        if (cancelled) return;
        const dt = Math.min(64, now - last); last = now;
        const heads = [], trails = [];
        arr.forEach((g, idx) => {
          let lng, lat;
          if (g.fallback) {
            g.t += g.spd * dt * slowFactor(g.a[0] + (g.b[0] - g.a[0]) * g.t, g.a[1] + (g.b[1] - g.a[1]) * g.t);
            if (g.t >= 1) { g.a = g.b; g.b = g.randPt(); g.t = 0; g.trail = []; }
            lng = g.a[0] + (g.b[0] - g.a[0]) * g.t;
            lat = g.a[1] + (g.b[1] - g.a[1]) * g.t;
          } else {
            const cur = posAt(g);
            g.dist += g.spd * dt * slowFactor(cur[0], cur[1]);
            if (g.dist >= g.total) { const w = makeWalker(pickRoute()); w.dist = 0; arr[idx] = w; return; }
            const p = posAt(g); lng = p[0]; lat = p[1];
          }
          g.trail.push([lng, lat]);
          if (g.trail.length > 16) g.trail.shift();
          heads.push({ type: "Feature", geometry: { type: "Point", coordinates: [lng, lat] }, properties: {} });
          if (g.trail.length > 1) trails.push({ type: "Feature", geometry: { type: "LineString", coordinates: g.trail.slice() }, properties: {} });
        });
        const gs = m.getSource("ghosts"); if (gs) gs.setData({ type: "FeatureCollection", features: heads });
        const ts = m.getSource("ghost-trails"); if (ts) ts.setData({ type: "FeatureCollection", features: trails });
        raf = requestAnimationFrame(frame);
      };
      raf = requestAnimationFrame(frame);
    };
    // Polling di mapRef.current: robusto al re-mount (StrictMode) e al timing dello stile.
    const boot = () => {
      if (cancelled) return;
      const m = mapRef.current;
      if (!m || !m.isStyleLoaded()) { if (tries++ < 80) setTimeout(boot, 120); return; }
      ensure(m);
      if (!n || reduce || (routes.length === 0 && seeds.length < 2)) { ["ghosts", "ghost-trails"].forEach((s) => { const src = m.getSource(s); if (src) src.setData(emptyFC); }); return; }
      animate(m);
    };
    boot();
    return () => { cancelled = true; if (raf) cancelAnimationFrame(raf); };
  }, [ghosts, ghostRoutes, ghostSeeds, trafficRoutes, status]);

  // "Traffico" sulle strade: segmenti congestionati (rosso pulsante) per dare vita e realismo.
  useEffect(() => {
    if (status !== "ready") return;
    const routes = (trafficRoutes || []).filter((r) => Array.isArray(r) && r.length >= 2);
    let cancelled = false, tries = 0, pulse = null;
    const boot = () => {
      if (cancelled) return;
      const m = mapRef.current;
      if (!m || !m.isStyleLoaded()) { if (tries++ < 80) setTimeout(boot, 120); return; }
      const fc = { type: "FeatureCollection", features: routes.map((r) => ({ type: "Feature", geometry: { type: "LineString", coordinates: r }, properties: {} })) };
      if (!m.getSource("traffic")) {
        m.addSource("traffic", { type: "geojson", data: fc });
        m.addLayer({ id: "traffic-casing", type: "line", source: "traffic", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#7F1D1D", "line-width": 9, "line-opacity": 0.25, "line-blur": 3 } });
        m.addLayer({ id: "traffic-line", type: "line", source: "traffic", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#EF4444", "line-width": 4, "line-opacity": 0.6, "line-dasharray": [1.4, 1.1] } });
      } else {
        m.getSource("traffic").setData(fc);
      }
      let t = 0;
      pulse = setInterval(() => {
        if (cancelled || !m.getLayer("traffic-line")) return;
        t += 0.16;
        m.setPaintProperty("traffic-line", "line-opacity", 0.45 + Math.sin(t) * 0.22);
      }, 120);
    };
    boot();
    return () => { cancelled = true; if (pulse) clearInterval(pulse); };
  }, [trafficRoutes, status]);

  return (
    <div className="explore-map-wrap">
      <div ref={ref} className="explore-map" data-testid="explore-map" />
      <div className={`daynight daynight--${phase}`} data-testid="map-daynight" aria-hidden="true" />
      <div className="dusk-sky" />
      <div className="dusk-vignette" />

      {status === "loading" && (
        <div className="map-overlay" data-testid="map-loading">
          <div className="map-spinner" />
          <div className="map-overlay-text">Caricamento mappa…</div>
        </div>
      )}
      {status === "error" && (
        <div className="map-overlay" data-testid="map-error">
          <div className="map-overlay-title">Mappa temporaneamente non disponibile</div>
          <div className="map-overlay-text">Non riusciamo a caricare la cartografia in questo momento.</div>
          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap", justifyContent: "center" }}>
            <button className="map-retry-btn" data-testid="map-retry" onClick={() => retry(false)}>Riprova</button>
            <a className="map-retry-btn" data-testid="map-home-smart" href="/home" style={{ textDecoration: "none" }}>Vai a Home Smart</a>
          </div>
        </div>
      )}
    </div>
  );
}
